import React, { useContext, useEffect, useRef } from 'react';
import { compose, withState, pure } from 'recompose';
import _ from 'lodash';
import difference from 'lodash/difference';
import get from 'lodash/get';
import size from 'lodash/size';
import { styled, useStyletron } from 'baseui';
import { Block } from 'baseui/block';
import { QuestionStyleContext } from '../../question-style-context';
import { Button, SIZE as BUTTON_SIZE, KIND as BUTTON_KIND, SHAPE as BUTTON_SHAPE } from 'baseui/button';
import { Input, SIZE } from 'baseui/input';
import { Select } from 'baseui/select';
import { Textarea } from 'baseui/textarea';
import { FileUploaderBasic, type FileUploaderBasicOverrides } from 'baseui/file-uploader-basic';
import { List, arrayMove } from 'baseui/dnd-list';
import { Slider } from 'baseui/slider';
import { FlexGrid, FlexGridItem } from 'baseui/flex-grid';
import RemoveIcon from '@uber/icons/x-small-filled';
import { QuillRichTextEditor, RichTextEditor } from '@uber/rich-text-editor-lib';
import type { QnAStateType, QuestionType } from '../../reducers';
import { SelectSubType, questionTypes, NumberSubType, validationTypes, SliderLabels, InputSubType } from '../../reducers';
import { arrayToSelectOptions } from '../../utils/select';
import { getUserAnswersByQuestionId } from '../../reducers/selectors';
import { gutter } from '../../utils/styles';
import { isSelected, isEmpty } from '../../utils/answer';
import UserNote from './UserNote';
import type { Theme } from 'baseui';
const AnswerList = styled('ul', ({
  $theme
}) => ({
  padding: 0,
  margin: 0,
  background: $theme.colors.background,
  color: $theme.colors.primaryA
}));
const getInputOverrides = (theme: Theme, hasDynamicSizing?: boolean) => {
  return {
    Input: {
      style: {
        minHeight: theme.sizing.scale1200,
        height: hasDynamicSizing ? 'auto' : theme.sizing.scale1200,
        maxHeight: hasDynamicSizing ? theme.sizing.scale2400 : 'auto',
        fieldSizing: hasDynamicSizing ? 'content' : 'fixed',
        resize: 'vertical',
        fontSize: theme.sizing.scale600,
        borderTopWidth: 0,
        borderLeftWidth: 0,
        borderRightWidth: 0,
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
        width: '100%',
        paddingLeft: '5px',
        paddingRight: '5px',
        paddingTop: '5px',
        paddingBottom: '5px'
      },
      props: {
        'data-testid': 'answerInput'
      }
    },
    Root: {
      style: {
        borderRadius: theme.borders.radius300
      }
    },
    InputContainer: {
      style: {
        borderTopWidth: 0,
        borderLeftWidth: 0,
        borderRightWidth: 0,
        borderBottomWidth: 0
      }
    }
  };
};
const TickBarDiv = styled('div', ({
  $theme
}) => ({
  color: $theme.colors.contentPrimary,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  paddingRight: $theme.sizing.scale600,
  paddingLeft: $theme.sizing.scale600,
  paddingBottom: $theme.sizing.scale400
}));
const TickBarOverride = (sliderLabels?: string[]) => {
  return {
    TickBar: ({
      $min,
      $max
    }) => {
      const tickBars: JSX.Element[] = [];
      if (sliderLabels) {
        sliderLabels.forEach(label => {
          tickBars.push(<div>{label}</div>);
        });
      } else {
        for (let i = $min; i <= $max; i += ($max - $min) / 4) {
          tickBars.push(<div>{i}</div>);
        }
      }
      return <TickBarDiv>{tickBars}</TickBarDiv>;
    }
  };
};
const SliderOverrides = {
  InnerThumb: ({
    $value,
    $thumbIndex
  }) => <>{$value[$thumbIndex]}</>,
  ThumbValue: () => null,
  Thumb: {
    style: ({
      $theme
    }) => ({
      color: $theme.colors.contentPrimary,
      backgroundColor: $theme.colors.backgroundPrimary,
      border: `${$theme.sizing.scale0} solid ${$theme.colors.borderSelected}`,
      boxShadow: `0 1px 4px rgba(0, 0, 0, 0.12)`
    })
  },
  Mark: {
    style: ({
      $theme
    }) => ({
      backgroundColor: $theme.colors.backgroundSecondary
    })
  },
  Track: {
    style: ({
      $theme
    }) => ({
      paddingTop: $theme.sizing.scale800
    })
  }
};
const FileUploadOverrides: FileUploaderBasicOverrides = {
  Root: {
    style: ({
      $theme
    }) => ({
      marginTop: $theme.sizing.scale300
    })
  },
  ButtonComponent: {
    props: {
      overrides: {
        BaseButton: {
          style: ({
            $theme
          }) => ({
            marginTop: 0,
            borderRadiusTopLeft: $theme.sizing.scale0,
            borderRadiusTopRight: $theme.sizing.scale0,
            borderRadiusBottomLeft: $theme.sizing.scale0,
            borderRadiusBottomRight: $theme.sizing.scale0,
            marginLeft: $theme.sizing.scale400,
            paddingTop: $theme.sizing.scale100,
            paddingRight: $theme.sizing.scale600,
            paddingBottom: $theme.sizing.scale100,
            paddingLeft: $theme.sizing.scale600
          })
        }
      },
      kind: BUTTON_KIND.secondary
    }
  },
  FileDragAndDrop: {
    style: ({
      $theme
    }) => ({
      display: 'flex',
      flexDirection: 'row',
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: $theme.sizing.scale200,
      paddingRight: 0,
      overflow: 'hidden',
      justifyContent: 'space-between'
    })
  }
};
const DragAndRankListOverrides = {
  Item: {
    style: ({
      $theme
    }) => ({
      padding: $theme.sizing.scale500,
      borderRadius: $theme.borders.radius300
    })
  },
  Label: {
    style: ({
      $theme
    }) => ({
      fontSize: $theme.sizing.scale500
    })
  }
};
type AnswerItemProps = {
  type?: string | null;
  selected?: boolean | null;
  $theme: any;
  answerTextStyles?: Record<string, string | number>;
};
const AnswerItem = styled<typeof Block, AnswerItemProps>(Block, (p: AnswerItemProps) => {
  const commonStyle = {
    cursor: 'pointer',
    border: '1px solid #eee',
    padding: '0 5px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: gutter,
    background: p.$theme.colors.backgroundSecondary,
    color: p.$theme.colors.primaryA,
    borderRadius: p.$theme.borders.radius300,
    ...(p.answerTextStyles || {})
  };
  // input
  if (p.type === questionTypes.input || p.type === questionTypes.number || p.type === questionTypes.richText) {
    return {
      ...commonStyle,
      padding: 0
    };
  }

  // option list
  return {
    ...commonStyle,
    justifyContent: 'space-around',
    minHeight: p.$theme.sizing.scale900,
    height: 'auto',
    border: p.selected ? `3px solid ${p.$theme.colors.borderSelected}` : 'none',
    minWidth: 'fit-content',
    padding: `${p.$theme.sizing.scale200} ${p.$theme.sizing.scale400}`,
    marginTop: p.$theme.sizing.scale400
  };
});
const richTextStyles = {
  minHeight: '100px'
};

// function to check if there is a difference between two arrays irrespective of order
const changeInAnswers = (answerItems: string[], userAnswers: string[]) => {
  return !_.isEmpty(_.xor(answerItems, userAnswers));
};
const Answers = ({
  state,
  question,
  updateAnswers,
  selectAnswer,
  removeAnswer,
  updateNote,
  setCustomInput,
  customInput,
  shouldForceUpdateRTEAnswer,
  setForceUpdateRTEAnswer,
  showAnswerAsSelectLimit,
  isComposeMode,
  enableAutoFocusForRTE,
  readOnly = false
}: {
  state: QnAStateType;
  question: QuestionType;
  updateAnswers: Function;
  selectAnswer: Function;
  removeAnswer: Function;
  updateNote: Function;
  setCustomInput: Function;
  customInput: string;
  shouldForceUpdateRTEAnswer?: boolean;
  setForceUpdateRTEAnswer?: (prevValue: boolean) => void;
  showAnswerAsSelectLimit?: number;
  isComposeMode?: boolean;
  enableAutoFocusForRTE?: boolean;
  readOnly?: boolean;
}) => {
  const questionId = get(question, 'id');
  const type = get(question, 'type');
  const limit = get(question, 'limit');
  const subType = (get(question, 'tags').find(tag => tag === SelectSubType.drag_and_rank || tag === NumberSubType.rating_slider) as SelectSubType | NumberSubType | undefined);
  const shouldDisablePaste = get(question, 'tags').find(tag => tag === InputSubType.shouldDisablePaste);
  const hasDynamicSizing = get(question, 'tags').find(tag => tag === InputSubType.hasDynamicSizing);
  const answerItems = get(question, 'answers') || [];
  const userAnswers = getUserAnswersByQuestionId(state, questionId);
  const [_, theme] = useStyletron();
  const {
    answerTextStyles
  } = useContext(QuestionStyleContext);
  const selectGridColumns = parseInt(get(question, 'tags').find(tag => tag.startsWith('grid: '))?.substring('grid: '.length) ?? '0');
  const selectGridColumnsCount = Number.isNaN(selectGridColumns) ? 1 : selectGridColumns;

  // for rating slider
  const validations = get(question, 'validations');
  const parsedMin = parseInt(validations?.find(validation => validation?.type === validationTypes.MINVAL)?.value ?? '1');
  const parsedMax = parseInt(validations?.find(validation => validation?.type === validationTypes.MAXVAL)?.value ?? '5');
  const minRating = Number.isNaN(parsedMin) ? 1 : parsedMin;
  const maxRating = Number.isNaN(parsedMax) || parsedMax <= minRating ? minRating + 1 : parsedMax;
  let rating = parseInt((userAnswers as string[])[0]);
  const sliderLabels = subType === NumberSubType.rating_slider ? get(question, 'tags').find(tag => tag.startsWith(SliderLabels.labels))?.substring((SliderLabels.labels + ': ').length).split(',') : undefined;
  // For rating slider, we have to set default rating value to minRating if userAnswers is empty or NaN
  if (subType === NumberSubType.rating_slider) {
    if (Number.isNaN(rating)) {
      rating = minRating;
    } else {
      rating = Math.min(Math.max(rating, minRating), maxRating);
    }
  }

  // For drag and rank questions, we have to update userAnswers at initial render, in order to show list with correct answer options at UI
  if (subType === SelectSubType.drag_and_rank && changeInAnswers(answerItems.map(item => item.answer), (userAnswers as string[]))) {
    updateAnswers({
      questionId,
      question: get(question, 'question'),
      answers: answerItems.map(item => item.answer)
    });
  }
  useEffect(() => {
    if ((type === questionTypes.input || type === questionTypes.number || type === questionTypes.richText) && userAnswers?.[0] !== customInput) {
      setCustomInput(userAnswers?.[0]);
    }
  }, [userAnswers]);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  useEffect(() => {
    const currentInputRef = inputRef?.current;
    const handlePaste = e => {
      if (!shouldDisablePaste) return;
      e.preventDefault();
      alert('Pasting is disabled!');
    };
    currentInputRef?.addEventListener('paste', handlePaste);
    return () => currentInputRef?.removeEventListener('paste', handlePaste);
  }, [shouldDisablePaste]);
  const isFileUploadQues = type === questionTypes.file;
  return <AnswerList>
      {isFileUploadQues ? userAnswers.map((answerItem, idx) =>
    // @ts-expect-error todo(ts-migration) TS2741 Property '$theme' is missing in type '{ children: (string | Element)[]; key: number; as: string; href: string; target: string; title: string; type: "file"; selected: true; }' but required in type 'Ans...
    <AnswerItem key={idx} as="a" href={answerItem instanceof File ? URL.createObjectURL(answerItem) : answerItem} target="_blank" title="Click to open file in new tab" type={type} selected={true}>
            {answerItem instanceof File ? answerItem.name : answerItem}
            <Block display="flex">
              <Button size={BUTTON_SIZE.compact} kind={BUTTON_KIND.secondary} shape={BUTTON_SHAPE.round} onClick={e => {
          e.preventDefault();
          removeAnswer({
            qnaState: state,
            questionId,
            answer: answerItem
          });
        }} overrides={{
          BaseButton: {
            style: ({
              $theme
            }) => ({
              paddingTop: $theme.sizing.scale200,
              paddingBottom: $theme.sizing.scale200,
              paddingLeft: $theme.sizing.scale200,
              paddingRight: $theme.sizing.scale200
            })
          }
        }}>
                <RemoveIcon />
              </Button>
            </Block>
          </AnswerItem>) : subType === SelectSubType.drag_and_rank ? <List items={(userAnswers as string[])} onChange={({
      oldIndex,
      newIndex
    }) => {
      updateAnswers({
        qnaState: state,
        questionId,
        question: get(question, 'question'),
        answers: arrayMove(userAnswers, oldIndex, newIndex)
      });
    }} overrides={DragAndRankListOverrides} /> :
    // @ts-expect-error todo(ts-migration) TS2345 Argument of type 'number | undefined' is not assignable to parameter of type 'number'.
    !isFinite(showAnswerAsSelectLimit) || selectGridColumnsCount > 1 || showAnswerAsSelectLimit && size(answerItems) <= showAnswerAsSelectLimit ? <FlexGrid {...selectGridColumnsCount > 1 ? {
      flexGridColumnCount: selectGridColumns,
      flexGridColumnGap: 'scale400',
      flexGridRowGap: 'scale400',
      'data-testid': 'flex-grid'
    } : {}}>
          {answerItems.map((answerItem, idx) => {
        const selected = isSelected(userAnswers, answerItem.answer);
        return <FlexGridItem key={idx} overflow={'auto'}>
                {/* @ts-expect-error todo(ts-migration) TS2741 Property '$theme' is missing in type '{ children: string; key: number; as: string; selected: boolean; onClick: () => void; }' but required in type 'AnswerItemProps'. */}
                <AnswerItem key={idx} as="li" selected={selected} answerTextStyles={answerTextStyles} onClick={() => {
            if (selected) {
              removeAnswer({
                qnaState: state,
                questionId,
                answer: answerItem.answer
              });
            } else {
              selectAnswer({
                qnaState: state,
                questionId,
                question: get(question, 'question'),
                answer: answerItem.answer,
                type,
                limit
              });
            }
          }}>
                  {answerItem.answer}
                </AnswerItem>
              </FlexGridItem>;
      })}
        </FlexGrid> : <Select multi={limit !== 1} options={answerItems} labelKey={'answer'} valueKey={'answer'} value={arrayToSelectOptions({
      // @ts-expect-error todo(ts-migration) TS2322 Type 'UserAnswersT' is not assignable to type 'string[]'.
      values: userAnswers,
      labelKey: 'answer'
    }).reverse()} placeholder="Search Answers..." maxDropdownHeight="400px" overrides={{
      SingleValue: {
        style: ({
          $theme
        }) => {
          return {
            whiteSpace: 'pre-wrap'
          };
        }
      }
    }} onChange={({
      value
    }) => {
      const newAnswers = value.map(v => v.answer);
      const newItems = difference(newAnswers, userAnswers);
      const removedItems = difference(userAnswers, newAnswers);
      newItems.forEach(answer => {
        selectAnswer({
          qnaState: state,
          questionId,
          question: get(question, 'question'),
          answer,
          type,
          limit
        });
      });
      removedItems.forEach(answer => {
        removeAnswer({
          qnaState: state,
          questionId,
          answer
        });
      });
    }} />}
      {type === questionTypes.input &&
    // @ts-expect-error todo(ts-migration) TS2741 Property '$theme' is missing in type '{ children: Element; key: string; type: "input"; selected: boolean; }' but required in type 'AnswerItemProps'.
    <AnswerItem key={`type-in-${questionId}`} type={type} selected={isSelected(userAnswers, customInput)}>
          <Textarea value={customInput} overrides={getInputOverrides(theme, !!hasDynamicSizing)} placeholder="type in ..." size={SIZE.compact} disabled={readOnly}
      // Prevent Alt+S from bubbling to f7-utask's global hotkey listener,
      // which would trigger accidental task submission while typing.
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.altKey && e.key === 's') {
          e.preventDefault();
          e.stopPropagation();
        }
      }} onChange={e => {
        setCustomInput(e.target.value);
        selectAnswer({
          qnaState: state,
          questionId,
          question: get(question, 'question'),
          answer: e.target.value,
          type,
          limit
        });
      }} inputRef={inputRef} />
        </AnswerItem>}
      {type === questionTypes.richText &&
    // @ts-expect-error todo(ts-migration) TS2741 Property '$theme' is missing in type '{ children: Element; key: string; type: "input"; selected: boolean; }' but required in type 'AnswerItemProps'.
    <AnswerItem key={`type-in-${questionId}`} type={type} selected={isSelected(userAnswers, customInput)}>
          {isComposeMode ? <QuillRichTextEditor text={customInput} onChange={({
        markdown
      }) => {
        setCustomInput(markdown);
        selectAnswer({
          qnaState: state,
          questionId,
          question: get(question, 'question'),
          answer: markdown,
          type,
          limit
        });
      }} styles={richTextStyles} config={{
        disablePaste: !!shouldDisablePaste
      }} enableAutoFocusForRTE={enableAutoFocusForRTE} readOnly={readOnly} /> : <RichTextEditor text={customInput} onChange={({
        markdown
      }) => {
        setCustomInput(markdown);
        selectAnswer({
          qnaState: state,
          questionId,
          question: get(question, 'question'),
          answer: markdown,
          type,
          limit
        });
      }} shouldForceUpdateRTEAnswer={shouldForceUpdateRTEAnswer} setForceUpdateRTEAnswer={setForceUpdateRTEAnswer} styles={richTextStyles} config={{
        disablePaste: !!shouldDisablePaste
      }} readOnly={readOnly} />}
        </AnswerItem>}
      {type === questionTypes.number &&
    // @ts-expect-error todo(ts-migration) TS2741 Property '$theme' is missing in type '{ children: Element; key: string; type: "number"; selected: boolean; }' but required in type 'AnswerItemProps'.
    <AnswerItem key={`type-in-${questionId}`} type={type} selected={isSelected(userAnswers, customInput)}>
          {subType === NumberSubType.rating_slider ? <Slider value={[rating]} min={minRating} max={maxRating} marks={Math.abs(maxRating - minRating + 1) <= 15 ? true : false} persistentThumb onChange={({
        value
      }) => {
        selectAnswer({
          qnaState: state,
          questionId,
          question: get(question, 'question'),
          answer: value[0].toString(),
          type,
          limit
        });
      }} overrides={{
        ...SliderOverrides,
        ...TickBarOverride(sliderLabels)
      }} /> : <Input value={customInput} overrides={getInputOverrides(theme)} placeholder="Enter numeric value" type="number" size={SIZE.mini} onChange={e => {
        setCustomInput(e.target.value);
        selectAnswer({
          qnaState: state,
          questionId,
          question: get(question, 'question'),
          answer: e.target.value,
          type,
          limit
        });
      }} />}
        </AnswerItem>}
      {isFileUploadQues && isEmpty(userAnswers) && <FileUploaderBasic onDrop={(acceptedFiles, rejectedFiles) => {
      if (acceptedFiles.length > 0) {
        selectAnswer({
          qnaState: state,
          questionId,
          question: get(question, 'question'),
          answer: acceptedFiles[0],
          type,
          limit
        });
      }
    }} overrides={FileUploadOverrides} />}
      <UserNote state={state} question={question}
    // @ts-expect-error todo(ts-migration) TS2322 Type 'Function' is not assignable to type '(a: UpdateNotePayload) => void'.
    updateNote={updateNote} userAnswers={userAnswers} />
    </AnswerList>;
};
export default compose(pure, withState('customInput', 'setCustomInput', ({
  state,
  question
}) => {
  const questionId = get(question, 'id');
  const userAnswers = getUserAnswersByQuestionId(state, questionId);
  return userAnswers[0] || '';
}))(Answers);